import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type * as React from "react";
import cn from "twsx";

// Figma headings (Linear measured): 13/20 card and group, 15/24 section and
// dialog, 20/28 page, 24/32 hero (20/28 on mobile). The level sets the element and, unless a
// size is given, the size: h1 → lg, h2 → md, h3–h6 → sm.
export const headingVariants = cva("", {
  variants: {
    size: {
      sm: "text-base leading-5",
      md: "text-lg leading-6",
      lg: "text-2xl leading-7 [letter-spacing:var(--title-2-letter-spacing)]",
      xl: "text-3xl leading-8 [letter-spacing:var(--title-3-letter-spacing)] max-md:text-2xl max-md:leading-7"
    },
    weight: {
      medium: "font-medium",
      semibold: "font-semibold"
    },
    tone: {
      primary: "text-text-primary",
      secondary: "text-text-secondary",
      tertiary: "text-text-tertiary",
      inverse: "text-text-on-accent"
    },
    truncate: {
      true: "min-w-0 truncate",
      false: ""
    }
  },
  defaultVariants: {
    weight: "semibold",
    tone: "primary",
    truncate: false
  }
});
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
const defaultSize = {
  1: "lg",
  2: "md",
  3: "sm",
  4: "sm",
  5: "sm",
  6: "sm"
} as const;
export type HeadingProps = VariantProps<typeof headingVariants> & Omit<React.HTMLAttributes<HTMLHeadingElement>, "color"> & {
  level: HeadingLevel;
  ref?: React.Ref<HTMLHeadingElement>;
};

/** Use when titling a page, section, card or dialog. */
export function Heading(t0) {
  const $ = _c(19);
  let className;
  let level;
  let props;
  let size;
  let tone;
  let truncate;
  let weight;
  if ($[0] !== t0) {
    ({
      level,
      size,
      weight,
      tone,
      truncate,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = level;
    $[3] = props;
    $[4] = size;
    $[5] = tone;
    $[6] = truncate;
    $[7] = weight;
  } else {
    className = $[1];
    level = $[2];
    props = $[3];
    size = $[4];
    tone = $[5];
    truncate = $[6];
    weight = $[7];
  }
  const Element = `h${level}` as const;
  let t1;
  if ($[8] !== className || $[9] !== level || $[10] !== size || $[11] !== tone || $[12] !== truncate || $[13] !== weight) {
    t1 = cn(headingVariants({
      size: size ?? defaultSize[level],
      weight,
      tone,
      truncate
    }), className);
    $[8] = className;
    $[9] = level;
    $[10] = size;
    $[11] = tone;
    $[12] = truncate;
    $[13] = weight;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== Element || $[16] !== props || $[17] !== t1) {
    t2 = <Element className={t1} {...props} />;
    $[15] = Element;
    $[16] = props;
    $[17] = t1;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
}
export default Heading;